import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { clientStaffEditSchema } from '../../validation/users';
import { gray100, gray80, primaryButtonBackgroundColor, red50 } from '../../constants/colors';
import { sizeBelowTenMb, textMb, titleMb, w100 } from '../../constants/location';

import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomRadioButton from './CustomRadioButton';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import { doc, getDoc } from 'firebase/firestore';
import { editStaffUser, usersRef } from '../../api/firebase/firestore/user';
import { common } from '../../modules/error';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import {
  getAllGroups,
  addMemberToGroup,
  removeMemberFromGroup,
} from '../../api/firebase/firestore/group';
import type { GroupType } from '../../validation/group/groupType';
import { GroupContext } from '../../contexts/group';

const styles = {
  cardSize: {
    '@media screen and (max-width:480px)': {
      width: 343,
    },
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
  },
  cardContentPadding: {
    '&:last-child': {
      '@media screen and (max-width:480px)': {
        paddingBottom: '32px',
      },
      paddingBottom: '40px',
    },
    '@media screen and (max-width:480px)': {
      padding: '32px 16px',
    },
    padding: '40px',
  },
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0,
    },
    marginBottom: '16px',
  },
  required: {
    color: red50,
    marginLeft: '10px',
  },
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px',
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute',
  },
  submitButton: {
    height: '42px',
    color: primaryButtonBackgroundColor,
  },
  textMb,
  textSmall: {
    fontSize: '11.5px',
  },
  titleMb,
  w100,
};

export interface EditStaffType {
  email?: string | null | undefined;
  name: string;
  department: string;
  role: boolean;
  icon?: File | undefined;
  daily_limit?: number | null;
}

const StaffEditCard: React.FC = () => {
  const navigate = useNavigate();
  const { refreshGroups } = useContext(GroupContext);
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const location = useLocation();
  const [uid] = useState<string>((location.state as { id: string })?.id);
  const [isAdmin] = useState<boolean>((location.state as { isAdmin: boolean })?.isAdmin);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [initialGroupIds, setInitialGroupIds] = useState<string[]>([]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditStaffType>({
    defaultValues: {
      department: '',
      name: '',
      role: false,
      daily_limit: null,
    },
    resolver: yupResolver(clientStaffEditSchema),
  });

  useEffect(() => {
    void (async (): Promise<void> => {
      setLoading(true);
      if (uid) {
        const [userSnap, allGroups] = await Promise.all([
          getDoc(doc(usersRef, uid)),
          getAllGroups().catch(() => [] as GroupType[]),
        ]);
        const user = userSnap.data();
        setPreviewIcon(user?.iconUrl);

        if (user) {
          reset({
            department: user?.department as string,
            email: user?.email as string,
            name: user?.name as string,
            role: Boolean(Number(user?.role) === 1),
            daily_limit: user?.daily_limit ?? null,
          });
        }

        // グループ一覧と現在の所属を設定
        setGroups(allGroups);
        const userGroupIds = allGroups.filter(g => g.memberUids.includes(uid)).map(g => g.groupId);
        setSelectedGroupIds(userGroupIds);
        setInitialGroupIds(userGroupIds);
      }
      setLoading(false);
    })();
  }, []);

  const boolToString = (isAdmin: boolean): string => (isAdmin ? '1' : '0');

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('name') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        watch('role') === undefined ||
        Boolean(errors.name) ||
        Boolean(errors.icon) ||
        Boolean(errors.department) ||
        Boolean(iconSizeError)
      );
    }
    return false;
  };

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [iconSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError('画像は10MBまでしか登録できません')
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadIconFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file && file.length > 0) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);
      await editStaffUser(uid, watch, s3FileIcon);

      // グループメンバーシップの差分更新
      const addedGroups = selectedGroupIds.filter(id => !initialGroupIds.includes(id));
      const removedGroups = initialGroupIds.filter(id => !selectedGroupIds.includes(id));
      await Promise.all([
        ...addedGroups.map(groupId => addMemberToGroup(groupId, uid)),
        ...removedGroups.map(groupId => removeMemberFromGroup(groupId, uid)),
      ]);

      // サイドバーのグループ一覧を更新
      if (addedGroups.length > 0 || removedGroups.length > 0) {
        await refreshGroups();
      }

      setOpen(true);
      setLoading(false);
      navigate('/staff/edit/complete');
    } catch (error) {
      let errorText: string;
      if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setFirstSubmit(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: gray100, textAlign: 'left' }]}
                component="div"
              >
                担当者編集
              </Typography>
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={iconSizeError}
                    helperText={errors.icon?.message}
                    formMb="24px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      field.onChange(value);
                      changeUploadIconFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText="メールアドレス"
                    required
                    requiredText="必須"
                    placeholder="xxx@cto-hands.co.jp"
                    disabled
                    formMb="24px"
                    sx={{ marginBottom: '24px', width: '100%' }}
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText="担当者名"
                    required
                    requiredText="必須"
                    formMb="24px"
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText="部署"
                    required
                    requiredText="必須"
                    formMb="24px"
                  />
                )}
                control={control}
                name="department"
              />
              {boolToString(isAdmin) === '1' && (
                <Controller
                  render={({ field }): React.ReactElement => (
                    <CustomRadioButton
                      {...field}
                      error={Boolean(errors.role)}
                      helperText={errors.role?.message}
                      labelText="権限"
                      required
                      requiredText="必須"
                      trueOption="管理者"
                      falseOption="メンバー"
                      trueOptionDescription="担当者の閲覧・招待・編集ができます。"
                      falseOptionDescription="担当者の閲覧ができます。"
                    />
                  )}
                  control={control}
                  name="role"
                />
              )}
              {isAdmin && (
                <Controller
                  render={({ field }): React.ReactElement => (
                    <CustomTextfield
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        const val = e.target.value;
                        field.onChange(val === '' ? null : Number(val));
                      }}
                      type="number"
                      error={Boolean(errors.daily_limit)}
                      helperText={errors.daily_limit?.message}
                      labelText="1日のアップロード上限"
                      placeholder="無制限"
                      formMb="8px"
                      inputProps={{ min: 1 }}
                    />
                  )}
                  control={control}
                  name="daily_limit"
                />
              )}
              {isAdmin && (
                <Typography
                  sx={{ fontSize: '11.5px', color: gray80, mb: '24px', textAlign: 'left' }}
                >
                  空欄の場合は無制限になります。
                </Typography>
              )}
              {isAdmin && groups.length > 0 && (
                <Box sx={{ mb: '16px', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                    <Typography sx={{ fontSize: '14px' }}>所属グループ</Typography>
                  </Box>
                  {groups.map(group => (
                    <FormControlLabel
                      key={group.groupId}
                      control={
                        <Checkbox
                          checked={selectedGroupIds.includes(group.groupId)}
                          onChange={(e): void => {
                            if (e.target.checked) {
                              setSelectedGroupIds(prev => [...prev, group.groupId]);
                            } else {
                              setSelectedGroupIds(prev => prev.filter(id => id !== group.groupId));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: gray80,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '300px',
                          }}
                        >
                          {group.name}
                        </Typography>
                      }
                      sx={{ display: 'flex', mr: 0 }}
                    />
                  ))}
                </Box>
              )}
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={{
                  ...styles.submitButton,
                  ...(boolToString(isAdmin) === '0' && { paddingTop: '8px' }),
                }}
                onClick={(): void => {
                  void (async (): Promise<void> => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (iconSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                  変更する
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={(): void => setOpen(false)}
            text={errorText}
            sx={styles.snackbar}
          />
        </Box>
      </Box>
    </>
  );
};

export default StaffEditCard;
